import React, {
  memo,
  useEffect,
  useRef
} from 'react';
import { useSelector } from 'react-redux';
import IncomingCallCard from './IncomingCallCard';
import useIncomingCallAccessDecision from '../../hooks/calls/useIncomingCallAccessDecision';
import { useWhatsappCallsRuntime } from '../../hooks/calls/WhatsappCallsRuntimeContext';
import shouldDismissUnauthorizedIncoming from '../../hooks/calls/incomingCallVisibilityPolicy';

const RINGING_CONTEXT = 'ringing';

/**
 * Resolves caller data and call assignment before showing IncomingCall.
 * Uses call permissions (receive_calls) + customer agent assignment only —
 * not chat permissions such as view_all_chats. Shared by Fonema and Voice.
 */
const IncomingCallAccessGate = memo(({
  callInfoData,
  startActiveCall,
  updateIncomingPhone,
  onDismiss
}) => {
  const { provider } = useWhatsappCallsRuntime();
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const activeVoiceCallId = useSelector((state) => (
    provider === 'voice' ? state.voiceReducer?.activeCall?.callControlId : null
  ));
  const pendingVoiceAnswerId = useSelector((state) => (
    provider === 'voice' ? state.voiceReducer?.currentCallControlId : null
  ));
  const dismissedRef = useRef(false);

  const {
    customer,
    isAuthorizationReady,
    canViewIncomingCall
  } = useIncomingCallAccessDecision({ callInfoData, updateIncomingPhone });

  useEffect(() => {
    if (!isAuthorizationReady || canViewIncomingCall || dismissedRef.current) return;

    if (!shouldDismissUnauthorizedIncoming({ canView: canViewIncomingCall, context: RINGING_CONTEXT })) {
      return;
    }

    dismissedRef.current = true;

    // eslint-disable-next-line no-console
    console.info('[VoiceSocket] IncomingCallAccessGate dismiss (call assignment/permissions)', {
      callId: callInfoData?.callId,
      provider,
      retailerUserId: currentRetailerUser?.id ?? null
    });

    if (typeof onDismiss === 'function') {
      onDismiss(callInfoData.callId);
    }
  }, [
    isAuthorizationReady,
    canViewIncomingCall,
    callInfoData?.callId,
    onDismiss,
    provider,
    currentRetailerUser?.id
  ]);

  if (!canViewIncomingCall || !isAuthorizationReady) {
    return null;
  }

  const blockingVoiceCallId = activeVoiceCallId || pendingVoiceAnswerId;
  if (blockingVoiceCallId && blockingVoiceCallId !== callInfoData?.callId) {
    return null;
  }

  const enrichedCallInfoData = customer
    ? { ...callInfoData, customer }
    : callInfoData;

  return (
    <IncomingCallCard
      callInfoData={enrichedCallInfoData}
      startActiveCall={startActiveCall}
    />
  );
});

IncomingCallAccessGate.displayName = 'IncomingCallAccessGate';

export default IncomingCallAccessGate;
